import { isDefined } from "@clipboard-health/util-ts";
import { ButtonBase, Stack } from "@mui/material";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { Icon } from "@src/appV2/redesign/components/Icon";
import { Text } from "@src/appV2/redesign/components/Text";
import { useDisplayTime } from "@src/appV2/redesign/components/LocalTime/useDisplayTime";
import { DEFAULT_TIMEZONE } from "@src/constants/timezone";

import { getCityFromTimezone, getTimezoneAbbreviation } from "./timezone";

interface SessionContextChipProps {
  /**
   * When provided, the chip renders as a button and invokes this handler on
   * click — used by admins to open the workplace/worker details drawer.
   */
  onClick?: () => void;
}

/**
 * Passive pill showing the entity being viewed (workplace or worker) + its
 * local time on a single line. Renders for any session with a profile loaded —
 * admins impersonating a workplace or worker, and workplace users themselves.
 * When `onClick` is passed, the chip becomes a button (admin affordance for
 * opening the details drawer).
 */
export function SessionContextChip({ onClick }: SessionContextChipProps = {}) {
  const { profile, tz, type } = useSession();
  const name = profile?.name;
  // Fall back to Pacific when the profile has no timezone — keeps the chip
  // showing a coherent time + abbreviation pair instead of going blank.
  const effectiveTz = tz ?? DEFAULT_TIMEZONE;
  const localTime = useDisplayTime({ timeZone: effectiveTz });

  if (!isDefined(name)) {
    return null;
  }

  const isWorker = type === "AGENT";
  const iconType = isWorker ? "person-male-formal" : "building";
  const detailsLabel = isWorker ? "worker" : "workplace";
  const city = getCityFromTimezone(effectiveTz);
  const tzAbbreviation = getTimezoneAbbreviation(effectiveTz);
  const showLocalInfo = isDefined(localTime);

  const content = (
    <Stack
      direction="row"
      alignItems="center"
      gap={2}
      sx={(theme) => ({
        paddingLeft: 3,
        paddingRight: 4,
        paddingY: 1,
        borderRadius: theme.borderRadius?.xxLarge,
        backgroundColor: theme.palette.navbar?.itemSelectedBackground,
        // Sets CSS `color`, which `Icon` inherits through `currentColor` on its
        // SVG stroke. (Icon's own `sx` callback isn't honored by the wrapper.)
        color: theme.palette.navbar?.itemContent,
        minWidth: 0,
        maxWidth: "26rem",
      })}
    >
      <Icon type={iconType} size="small" sx={{ flexShrink: 0 }} />
      <Text
        variant="body2"
        semibold
        noWrap
        sx={(theme) => ({ color: theme.palette.navbar?.userName, minWidth: 0 })}
      >
        {name}
      </Text>
      {showLocalInfo && (
        <>
          <Text
            variant="caption"
            inline
            sx={(theme) => ({ color: theme.palette.navbar?.sectionDivider, flexShrink: 0 })}
          >
            ·
          </Text>
          <Text
            variant="caption"
            noWrap
            sx={(theme) => ({
              color: theme.palette.navbar?.itemContent,
              flexShrink: 0,
              letterSpacing: "0.02em",
            })}
          >
            {localTime} {city}
            {isDefined(tzAbbreviation) && ` (${tzAbbreviation})`}
          </Text>
        </>
      )}
      {isDefined(onClick) && (
        <Icon type="chevron-right" size="small" sx={{ flexShrink: 0, marginLeft: 1 }} />
      )}
    </Stack>
  );

  if (!isDefined(onClick)) {
    return content;
  }

  return (
    <ButtonBase
      onClick={onClick}
      aria-label={`Open ${name} ${detailsLabel} details`}
      sx={(theme) => ({
        borderRadius: theme.borderRadius?.xxLarge,
        transition: theme.transitions.create("filter"),
        "&:hover": { filter: "brightness(1.1)" },
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.primary?.main}`,
          outlineOffset: 2,
        },
      })}
    >
      {content}
    </ButtonBase>
  );
}
